export const ZOOMME_PREFIX = 'ZoomMe:';
import { Currency, defined } from 'core';

export enum SendAddressType {
  ZOOMME = 'ZOOMME',
  CRYPTO_ADDRESS = 'CRYPTO_ADDRESS',
  CHILLED_VAULT = 'CHILLED_VAULT',
  TRADING_WALLET = 'TRADING_WALLET',
}

export type AddressDetail = {
  name: string;
  type: SendAddressType;
  network: string;
  address: string;
  displayName: string;
};

export type ApiAddress = {
  address: string | null;
  currencyCd: string;
  id: {
    name: string;
  };
  cryptoNetworkEn: string;
  zoomMeHandle: string | null;
};

export class SendAddress {
  constructor(private rawData: ApiAddress, public readonly chilledVault = false, public readonly tradingWallet = false) {}

  get name(): string {
    return this.rawData.id.name;
  }

  get address(): string {
    const address = this.rawData.address;
    return address ? `${this.rawData.cryptoNetworkEn}:${address}` : ZOOMME_PREFIX + this.rawData.zoomMeHandle;
  }

  get network(): string {
    return this.rawData.cryptoNetworkEn;
  }

  get submitAddress(): string {
    const address = this.rawData.address;
    return address ? address : ZOOMME_PREFIX + this.rawData.zoomMeHandle;
  }

  get type(): SendAddressType {
    if (this.chilledVault) {
      return SendAddressType.CHILLED_VAULT;
    } else if (this.tradingWallet) {
      return SendAddressType.TRADING_WALLET;
    } else if (this.rawData.address) {
      return SendAddressType.CRYPTO_ADDRESS;
    } else {
      return SendAddressType.ZOOMME;
    }
  }

  get displayName(): string {
    if (this.type === SendAddressType.CHILLED_VAULT || this.type === SendAddressType.TRADING_WALLET) {
      return this.name;
    } else {
      return `${this.name} - (${this.address})`;
    }
  }

  get addressDetail(): AddressDetail {
    return {
      name: this.name,
      type: this.type,
      network: this.rawData.cryptoNetworkEn,
      address: SendAddressType.CRYPTO_ADDRESS === this.type ? this.rawData.address! : this.rawData.zoomMeHandle!,
      displayName: this.displayName,
    };
  }

  // get cryptoNetwork: string | null
}

export class SendAddressForSave {
  type: SendAddressType;
  currencyCd: string;
  name: string;
  originalName: string;
  /**
   * This is the full name of address as it is sent to the server.
   */
  addressOrHandle: string;
  /**
   * This is the display address. For composite addresses (like EOS wallet addresses) it is the address part. For
   * non-composite addresses it is the same as the addressOrHandle
   */
  displayAddress: string;
  /**
   * This is only used for composite addresses (like EOS wallet addresses). It is the second part of the address
   * e.g. the EOS memoID or XRP tag.
   */
  displayId: string | null;
  // some coins are available via multiple networks
  network: string;

  invalidAddress: boolean;
  serverStatus: SaveAddressStatus;

  isValid(): boolean {
    if (this.type == SendAddressType.ZOOMME) {
      this.invalidAddress = !this.displayAddress.startsWith(ZOOMME_PREFIX);
    } else {
      this.invalidAddress = this.displayAddress.startsWith(ZOOMME_PREFIX);
    }

    return !this.invalidAddress;
  }

  bindWalletAddress(address: SendAddress): void {
    this.originalName = address.name;
    this.name = address.name;
    this.type = address.type;
    // TODO would probably be simpler to make ZoomMe a special network
    const rawAddress = address.addressDetail.address;
    this.addressOrHandle = SendAddressType.ZOOMME === address.type ? address.address : rawAddress;
    if (rawAddress.includes('?')) {
      const displayWalletAddress = rawAddress.split('?');
      this.displayAddress = displayWalletAddress[0];
      const displayWalletID = displayWalletAddress[1].split('=');
      this.displayId = displayWalletID[1];
    } else {
      // TODO would probably be simpler to make ZoomMe a special network
      this.displayAddress = this.addressOrHandle;
      this.displayId = null;
    }
    this.network = address.addressDetail.network;
  }

  updateAddressOrHandle(currency: Currency): void {
    if (currency.compositeAddress && this.haveDisplayId) {
      const formattedAddressID = currency.compositeAddressStringTagName + '=' + this.displayId;
      this.addressOrHandle = this.displayAddress.concat('?' + formattedAddressID);
    } else {
      this.addressOrHandle = this.displayAddress;
    }
  }

  get haveDisplayId(): boolean {
    return defined(this.displayId) && this.displayId.trim().length > 0;
  }
}

export enum SaveAddressStatus {
  Success = 'Success',
  InvalidZoomMeHandle = 'InvalidZoomMeHandle',
  DuplicateName = 'DuplicateName',
  InvalidExternalAddress = 'InvalidExternalAddress',
  Self = 'Self',
  Error = 'Error',
}

export class SaveAddressResponse {
  status: SaveAddressStatus;
}
